import { queryOptions } from '@tanstack/react-query'
import type { components } from '@/api/schema'
import type { QuoteBuilderApi } from '../api/client'
import type { Quote } from '../api/types'

type Schemas = components['schemas']

export type PricingOutreachList = Schemas['QbPricingOutreachList']
export type PricingOutreachRequest = Schemas['QbPricingOutreachRead']
export type PricingLineComparison = Schemas['QbPricingLineComparison']
export type OnlinePrices = Schemas['QbSellerObservations']
export type OnlinePrice = Schemas['QbPriceObservation']

/**
 * The worksheet read (demo `quoteApi.pricingOutreach`): manufacturer groups with their lines. On a
 * draft it also applies started manufacturer specials and reverts ended ones, which can move the
 * quote's revision.
 */
export const pricingWorksheetQueryOptions = (api: QuoteBuilderApi, quoteId: string) =>
  queryOptions({
    queryKey: ['pricing-outreach', quoteId],
    queryFn: ({ signal }) => api.request<PricingOutreachList>(`/quotes/${quoteId}/pricing-outreach`, { signal }),
  })

/** Online prices for one product from our crawl data (where the demo showed competitor pricing). */
export const onlinePricesQueryOptions = (api: QuoteBuilderApi, productId: number) =>
  queryOptions({
    queryKey: ['online-prices', productId],
    queryFn: ({ signal }) => api.request<OnlinePrices>(`/price-intelligence/products/${productId}/observations`, { signal }),
    staleTime: 60_000,
    retry: false,
  })

/** This customer's quotes, for earlier accepted prices of the same model. */
export const customerHistoryQueryOptions = (api: QuoteBuilderApi, customerId: number) =>
  queryOptions({
    queryKey: ['customer-history', customerId],
    queryFn: ({ signal }) => api.request<Quote[]>(`/quotes?customer_id=${customerId}`, { signal }),
    staleTime: 30_000,
    retry: false,
  })

export function pricingApi(api: QuoteBuilderApi) {
  const send = (path: string, method: string, quote: Quote, body: Record<string, unknown>) =>
    api.request<Quote>(path, { method, body: JSON.stringify({ expected_revision: quote.revision, ...body }) })
  return {
    /** One Margin or markup for every line (demo `quoteApi.updatePricing`). */
    updatePricing: (quote: Quote, changes: { margin_percent: string } | { markup_percent: string }) =>
      send(`/quotes/${quote.id}/pricing`, 'PATCH', quote, changes),
    updateLine: (quote: Quote, lineId: string, changes: Record<string, unknown>) =>
      send(`/quotes/${quote.id}/lines/${lineId}`, 'PATCH', quote, changes),
    updateRebate: (quote: Quote, rebate: string) => send(`/quotes/${quote.id}`, 'PATCH', quote, { rebate_amount: rebate }),
    read: (quoteId: string) => api.request<Quote>(`/quotes/${quoteId}`),
  }
}
